import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const money = (cents, currency) => `${new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', {minimumFractionDigits:2,maximumFractionDigits:2}).format(cents / 100)} ${currency === 'TRY' ? 'TL' : currency}`;

class SecondShirt extends HTMLElement {
  request;
  busy = false;
  connectedCallback() {
    this.hidden = !this.querySelector('[data-add-second]');
    this.addEventListener('click', this.add);
    document.addEventListener(StandardEvents.productSelect, this.select);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.cartChanged);
    window.addEventListener('pageshow', this.pageShow);
  }
  disconnectedCallback() {
    this.removeEventListener('click', this.add);
    document.removeEventListener(StandardEvents.productSelect, this.select);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.cartChanged);
    window.removeEventListener('pageshow', this.pageShow);
    this.request?.abort();
  }
  pageShow = event => { if (event.persisted) this.refresh(); };
  select = async event => {
    const picker = event.target.closest('variant-picker');
    if (!picker || picker.dataset.productId !== this.dataset.productId) return;
    this.hidden = true;
    this.request?.abort();
    const sequence = this.sequence = (this.sequence || 0) + 1;
    try {
      const result = await event.promise;
      if (sequence !== this.sequence || !this.isConnected) return;
      const id = result.detail?.resource?.id;
      if (id) { this.dataset.variantId = id; this.refresh(); }
    } catch {}
  };
  cartChanged = async event => {
    if (event.target === this || this.busy) return;
    this.hidden = true;
    try { await event.promise; this.refresh(); } catch {}
  };
  async fresh(signal) {
    const url = new URL(this.dataset.productUrl, location.origin);
    url.searchParams.set('section_id', this.dataset.sectionId);
    url.searchParams.set('variant', this.dataset.variantId);
    const response = await fetch(url, {cache:'no-store',signal});
    if (!response.ok) throw new Error();
    return new DOMParser().parseFromString(await response.text(),'text/html').querySelector('erten-second-shirt');
  }
  async refresh() {
    if (!this.isConnected || this.busy) return;
    this.hidden = true;
    this.request?.abort();
    this.request = new AbortController();
    try {
      const next = await this.fresh(this.request.signal);
      if (next && this.isConnected) this.replaceWith(next);
    } catch {}
  }
  add = async event => {
    const button = event.target.closest('[data-add-second]');
    if (!button || this.busy) return;
    this.busy = true;
    const label = button.textContent;
    this.querySelectorAll('[data-add-second]').forEach(item => item.disabled = true);
    button.textContent = this.dataset.adding;
    const status = this.querySelector('[data-second-status]');
    const link = this.querySelector('[data-second-cart]');
    status.textContent = '';
    let added = false;
    let deferred;
    try {
      const current = await this.fresh();
      const id = button.dataset.addSecond;
      if (!current || current.dataset.mode !== this.dataset.mode || !current.querySelector(`[data-add-second="${id}"]`)) {
        status.textContent = this.dataset.changed;
        this.busy = false;
        await this.refresh();
        return;
      }
      deferred = CartLinesUpdateEvent.createPromise();
      const items = [{id:Number(id),quantity:1}];
      if (this.dataset.mode === 'pair') items.unshift({id:Number(this.dataset.variantId),quantity:1});
      this.dispatchEvent(new CartLinesUpdateEvent({action:'add',context:'product',lines:items.map(item=>({merchandiseId:String(item.id),quantity:1})),promise:deferred.promise}));
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(item=>item.dataset.sectionId))].slice(0,5);
      const response = await fetch(this.dataset.addUrl,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items,sections,sections_url:location.pathname})});
      const result = await response.json();
      if (!response.ok || result.status) throw new Error();
      added = true;
      const cartResponse = await fetch(this.dataset.cartUrl,{cache:'no-store'});
      if (!cartResponse.ok) throw new Error();
      const cart = await cartResponse.json();
      const applied = (cart.cart_level_discount_applications || []).some(discount => discount.title === '2 ve Üzeri Üründe Sepete %15 İndirim' && discount.total_allocated_amount > 0);
      status.textContent = `${this.dataset.added} ${applied ? this.dataset.discount + ' ' : ''}${applied ? this.dataset.confirmed : this.dataset.total}: ${money(cart.total_price,cart.currency)}.`;
      link.hidden = false;
      this.querySelector('details')?.remove();
      deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,sections:result.sections,source:'erten-second-shirt',didError:false}});
      link.focus({preventScroll:true});
    } catch {
      status.textContent = added ? this.dataset.refresh : this.dataset.error;
      link.hidden = !added;
      if (added) this.querySelector('details')?.remove();
      deferred?.resolve({detail:{didError:true}});
    } finally {
      this.busy = false;
      if (!added) {
        this.querySelectorAll('[data-add-second]').forEach(item=>item.disabled = false);
        if (button.isConnected) button.textContent = label;
      }
    }
  };
}

class RecentProducts extends HTMLElement {
  products = [];
  connectedCallback() {
    this.load();
    this.observer = new MutationObserver(() => this.render());
    const main = this.closest('main');
    if (main) this.observer.observe(main,{childList:true,subtree:true});
    document.addEventListener(StandardEvents.cartLinesUpdate,this.cartChanged);
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener(StandardEvents.cartLinesUpdate,this.cartChanged);
  }
  cartChanged = async event => { try { await event.promise; this.render(); } catch {} };
  async load() {
    let history = [];
    try {
      const saved = JSON.parse(localStorage.getItem('erten-recent-handles') || '[]');
      if (Array.isArray(saved)) history = saved.filter(item=>typeof item?.handle === 'string' && /^[a-z0-9-]+$/.test(item.handle)).slice(0,12);
      localStorage.setItem('erten-recent-handles',JSON.stringify([{id:this.dataset.productId,handle:this.dataset.handle},...history.filter(item=>String(item.id)!==this.dataset.productId)].slice(0,12)));
    } catch { return; }
    const responses = await Promise.allSettled(history.filter(item=>String(item.id)!==this.dataset.productId).slice(0,8).map(async item=>{
      const response = await fetch(`${this.dataset.root.replace(/\/$/,'')}/products/${encodeURIComponent(item.handle)}.js`,{cache:'no-store'});
      if (!response.ok) return null;
      return response.json();
    }));
    if (!this.isConnected) return;
    this.products = responses.filter(result=>result.status==='fulfilled' && result.value?.available).map(result=>result.value);
    this.render();
  }
  render() {
    const excluded = new Set([this.dataset.productId,...[...document.querySelectorAll('erten-product-matches [data-recommended-product], erten-second-shirt [data-second-product]')].map(item=>item.dataset.recommendedProduct || item.dataset.secondProduct)]);
    const products = this.products.filter(product=>!excluded.has(String(product.id))).slice(0,4);
    const key = products.map(product=>`${product.id}:${product.price}`).join('|');
    if (key === this.lastKey) return;
    this.lastKey = key;
    const list = this.querySelector('ul');
    const cards = products.map(product=>{
      const li=document.createElement('li'); li.dataset.recentProduct=product.id;
      const link=document.createElement('a');link.href=`${this.dataset.root.replace(/\/$/,'')}/products/${encodeURIComponent(product.handle)}`;
      const image=document.createElement('img');const imageUrl=new URL(product.featured_image,location.origin);imageUrl.searchParams.set('width','480');image.src=imageUrl.href;image.alt=product.title;image.loading='lazy';image.width=300;image.height=400;
      const title=document.createElement('span');title.className='erten-recent__title';title.textContent=product.title;
      const price=document.createElement('span');price.className='erten-recent__price';price.textContent=money(product.price,document.querySelector('erten-second-shirt')?.dataset.currency || 'TRY');
      link.append(image,title,price);li.append(link);return li;
    });
    list.replaceChildren(...cards);
    this.hidden = products.length===0;
  }
}
if (!customElements.get('erten-second-shirt')) customElements.define('erten-second-shirt',SecondShirt);
if (!customElements.get('erten-recent-products')) customElements.define('erten-recent-products',RecentProducts);
